import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import AppButtons from "../AppButtons";
import { AppModalProps } from "./AppModal.props";
import {
  CancelRounded,
  CloseOutlined as CloseButtonIcon,
} from "@mui/icons-material";
import React from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { t } from "i18next";
import BookingGetters from "../../../store/onlineBooking/getters";

const getHeaderStyles = (crossIcon: boolean) => ({
  color: "white",
  backgroundColor: "black",
  borderRadius: "50px",
  width: "3%",
  marginLeft: "97%",
  height: "25px",
  padding: crossIcon ? "2%" : "",
});

const getButtonStyles = (crossIcon: boolean) => ({
  marginLeft: crossIcon ? "60%" : "38%",
  marginRight: "0%",
  height: "10px",
  paddingBottom: "16%",
  ...(crossIcon && { marginBottom: "60%", bottom: "5px" }),
});

const ModalHeader = ({
  handleClose,
  crossIcon,
}: {
  handleClose: () => void;
  crossIcon: boolean;
}) => (
  <header
    className="sticky top-0 h-full bg-white float-right flex justify-end"
    style={getHeaderStyles(crossIcon)}
  >
    <AppButtons
      disableElevation
      startIcon={<CloseButtonIcon />}
      color="clear"
      className="w-fit"
      clickHandler={handleClose}
      style={getButtonStyles(crossIcon)}
    />
  </header>
);

const ModalContent = ({ children }: { children: React.ReactNode }) => (
  <main className="overflow-auto">
    <div className="h-full overflow-auto">{children}</div>
  </main>
);

function AppModal({ children, handleClose, isOpen, crossIcon }: AppModalProps) {
  const handleDialogClose = (_: unknown, reason: string) => {
    if (reason !== "backdropClick") handleClose(true);
  };

  return (
    <div>
      <Dialog
        onClose={handleDialogClose}
        open={isOpen}
        className="mt-[-1%]"
        maxWidth="md"
        style={{ height: "676px" }}
      >
        <DialogContent>
          <div className="flex flex-col h-full pt-5">
            <ModalHeader handleClose={handleClose} crossIcon={crossIcon} />
            <ModalContent>{children}</ModalContent>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { AppModal };

function MapHeader({ handleClose, bookingDetails }) {
  return (
    <div className="flex justify-between p-[30px] text-white">
      <header className="flex items-center justify-center h-[12px] w-[32px] rounded-full absolute left-0 top-0 m-[20px] z-10">
        <AppButtons
          disableElevation
          startIcon={<CancelRounded />}
          color="clear"
          className="w-fit"
          clickHandler={handleClose}
          style={{ margin: 0, height: "24px" }}
        />
      </header>
      <div
        className="flex-1 pr-[125px] mt-[29px]"
        style={{ marginLeft: "32px" }}
      >
        <p className="text-md capitalize text-[#0078D6]">
          <img
            src="/assets/icons/Info_fill.png"
            className="h-[28px] w-[30px]"
            alt="icon"
          />
        </p>
        <LocationDetail
          text={bookingDetails?.pickUpLocationDetails?.dealerName}
        />
        <LocationDetail
          text={bookingDetails?.pickUpLocationDetails?.locationDescription}
        />
      </div>
    </div>
  );
}

function LocationDetail({ text }) {
  return (
    <div
      className="flex items-center mt-[1%] ml-[3%]"
      style={{ fontFamily: "MBCorpoAScreen1Cond-Regular-Web" }}
    >
      <span className="text-white whitespace-nowrap font-normal text-[20px] font-mbcorpo">
        {text}
      </span>
    </div>
  );
}

function MapContent({
  latitude,
  longitude,
  mapContainerStyle,
  mapOptions,
  handleMapClick,
  appSettings,
}) {
  return (
    <div className="p-[38px]" style={{ flex: "2" }} onClick={handleMapClick}>
      <LoadScript googleMapsApiKey={appSettings?.googleMapApiKey}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{ lat: latitude, lng: longitude }}
          zoom={12}
          options={mapOptions}
          onClick={handleMapClick}
        >
          <Marker
            position={{ lat: latitude, lng: longitude }}
            title="Vehicle Location"
          />
        </GoogleMap>
      </LoadScript>
    </div>
  );
}

export function MapModal({
  handleClose,
  isOpen,
  longitude,
  latitude,
  mapContainerStyle,
  mapOptions,
}) {
  const handleDialogClose = (_, reason) =>
    reason !== "backdropClick" && handleClose(true);
  const handleMapClick = (e) => e.stopPropagation();
  const bookingDetails = BookingGetters.getBookingDetails();
  const appSettings = BookingGetters.getAppSettings();

  return (
    <div className="modal-overlay">
      <Dialog
        onClose={handleDialogClose}
        open={isOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { transform: "none", position: "absolute", top: 0 },
        }}
      >
        <DialogContent style={{ padding: 0, backgroundColor: "#1E1E1E" }}>
          <div className="flex h-full">
            <MapHeader
              handleClose={handleClose}
              bookingDetails={bookingDetails}
            />
            <MapContent
              latitude={latitude}
              longitude={longitude}
              mapContainerStyle={mapContainerStyle}
              mapOptions={mapOptions}
              handleMapClick={handleMapClick}
              appSettings={appSettings}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function logoutModal({
  handleClose,
  isSmallScreen,
  open,
  handleLogout,
}) {
  const dialogStyles = {
    width: !isSmallScreen ? "500px" : "90%",
    maxWidth: "600px",
    padding: !isSmallScreen ? "20px" : "10px",
    transform: "none",
    position: "absolute",
    top: 0,
  };

  const buttonStyles1 = {
    gap: 1,
    fontWeight: "bold",
    textTransform: "capitalize",
    padding: "4px 8px",
    minWidth: "100px",
  };

  const cancelButtonStyles = {
    ...buttonStyles1,
    borderColor: "#7B7B7B",
    color: "#7B7B7B",
  };

  return (
    <div className="modal-overlay">
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="logout-confirmation-title"
        aria-describedby="logout-confirmation-description"
        PaperProps={{ sx: dialogStyles }}
      >
        <DialogContent>{t("Are you sure you want to log out?")}</DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={cancelButtonStyles}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={handleLogout} variant="contained" sx={buttonStyles1}>
            {t("Logout")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
